import { Injectable } from '@angular/core';
import {
  LookbookApiService,
  mapListGroupingTypeToSearchSort,
} from '@epicuro-next/data/engagement/engagement-api';
import { ProductListGroupingType } from '@epicuro-next/data/engagement/engagement-models';
import { LookBook } from '@epicuro-next/models/engagement';
import { upsertManyListEntities } from '@epicuro-next/state/list-entities';
import {
  selectAccountId,
  selectSiteId,
} from '@epicuro-next/state/session-state';
import { isNonNullish } from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { catchError, filter, map, mergeMap, take } from 'rxjs/operators';

import {
  loadLookbook,
  loadLookbookError,
  loadLookbookSuccess,
} from './lookbook-entities.actions';

@Injectable()
export class LookBookEntitiesEffects {
  constructor(
    private store: Store,
    private actions$: Actions,
    private lookbookApi: LookbookApiService,
  ) {}

  public loadLookBook$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadLookbook),
      map(({ lookBookId }) => lookBookId),
      filter(isNonNullish),
      concatLatestFrom(() => [
        this.store.pipe(select(selectAccountId), filter<string>(Boolean)),
        this.store.select(selectSiteId),
      ]),
      mergeMap(([lookBookId, accountId, siteId]) =>
        this.lookbookApi
          .getLookBook({
            queryparams: { lookBookId },
            accountId,
            siteId: siteId ?? '',
          })
          .pipe(
            take(1),
            map((lookBook) => {
              if (lookBook) {
                return loadLookbookSuccess({
                  lookBookId,
                  lookBook: lookBook as unknown as LookBook,
                });
              } else {
                return loadLookbookError({
                  lookBookId,
                  error: 'Not found',
                });
              }
            }),
            catchError((err) => [
              loadLookbookError({
                lookBookId,
                error: err.message,
              }),
            ]),
          ),
      ),
    ),
  );

  public upsertListEntities$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadLookbookSuccess),
      map(({ lookBook }) =>
        upsertManyListEntities({
          listEntities: lookBook.items.map((item) => ({
            listId: item.productListId,
            list: item.productList,
            loading: false,
            loadingProducts: false,
            reorderingCount: 0,
            sortType: mapListGroupingTypeToSearchSort(
              item.productList.groupingType as ProductListGroupingType,
            ),
          })),
        }),
      ),
    ),
  );
}
